import { HighlightText } from "@/components/MedicalChronology/HighlightText";
import { Activity, Sparkles, Stethoscope, TestTube } from "lucide-react";
import React, { type Dispatch, type SetStateAction } from "react";

import type { SummaryDraft } from "../types";
import { SummaryHighlightCard } from "./fields/SummaryHighlightCard";

export interface SummaryClinicalHighlightsSectionProps {
  isEditMode: boolean;
  /** From summary tab search: when true, highlight cards use plain text + highlights instead of markdown. */
  isSearching: boolean;
  summaryDraft: SummaryDraft;
  setSummaryDraft: Dispatch<SetStateAction<SummaryDraft>>;
  highlightQuery: string;
}

export const SummaryClinicalHighlightsSection: React.FC<SummaryClinicalHighlightsSectionProps> = ({
  isEditMode,
  isSearching,
  summaryDraft,
  setSummaryDraft,
  highlightQuery,
}) => (
  <section className="overflow-hidden rounded-xl border border-neutral-100 bg-card shadow-sm">
    <div className="flex items-center gap-2.5 border-b border-neutral-100 bg-gradient-to-r from-violet-50/60 via-white to-white px-5 py-3.5">
      <div className="flex items-center justify-center rounded-lg bg-violet-100/70 p-1.5 text-violet-600">
        <Sparkles size={14} strokeWidth={2.5} />
      </div>
      <div>
        <h3 className="text-xs font-bold tracking-tight text-neutral-900">
          <HighlightText text="Case highlights" highlightQuery={highlightQuery} />
        </h3>
        <p className="mt-0.5 text-2xs font-medium text-neutral-500">
          Injuries, test results &amp; diagnoses
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-3 p-4">
      <SummaryHighlightCard
        title="Key Injuries"
        icon={<Activity size={16} strokeWidth={2.5} className="text-primary" />}
        iconBg="bg-card ring-1 ring-primary-border"
        borderColor="border-l-primary-border"
        value={summaryDraft.key_injuries}
        isEditMode={isEditMode}
        onChange={(v) => setSummaryDraft((prev) => ({ ...prev, key_injuries: v || null }))}
        placeholder="Add key injuries (Markdown)…"
        isSearching={isSearching}
        highlightQuery={highlightQuery}
      />
      <SummaryHighlightCard
        title="Key Test Results"
        icon={<TestTube size={16} strokeWidth={2.5} className="text-primary" />}
        iconBg="bg-primary-subtle"
        borderColor="border-l-primary-border"
        value={summaryDraft.key_test_results}
        isEditMode={isEditMode}
        onChange={(v) => setSummaryDraft((prev) => ({ ...prev, key_test_results: v || null }))}
        placeholder="Add key test results (Markdown)…"
        isSearching={isSearching}
        highlightQuery={highlightQuery}
      />
      <SummaryHighlightCard
        title="Key Diagnoses"
        icon={<Stethoscope size={16} strokeWidth={2.5} className="text-primary-emphasis" />}
        iconBg="bg-primary-subtle-hover"
        borderColor="border-l-primary"
        value={summaryDraft.key_diagnoses}
        isEditMode={isEditMode}
        onChange={(v) => setSummaryDraft((prev) => ({ ...prev, key_diagnoses: v || null }))}
        placeholder="Add key diagnoses (Markdown)…"
        isSearching={isSearching}
        highlightQuery={highlightQuery}
      />
    </div>
  </section>
);
